/**
 * ProviderArrivalScreen - Tela "Chegada ao local" do prestador.
 *
 * Usa o GPS do aparelho para comparar com as coordenadas coladas na OS. A distância
 * exibida é só orientação: o servidor (_provider_portal_arrival) recalcula e aplica
 * raio, precisão máxima, política fora do raio e foto obrigatória do tenant.
 *
 * RELEASE: 2026-10-13 v1.0.0 - Versão inicial (BR-PP-022).
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Camera, CheckCircle2, Loader2, MapPin, Phone, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CameraCaptureDialog } from '@/components/checklist/inspection/CameraCaptureDialog';
import { cn } from '@/lib/utils';
import { LocationPermissionSheet as ProviderLocationPermissionSheet } from '@/components/common/LocationPermissionSheet';
import { arrivalWatermarkLines, geoDistanceM, providerArrivalState, providerContactLinks, providerPositionAgeS, providerPositionReusable } from '@/lib/providerPortalDisplay';

type Caller = <T>(action: string, payload?: Record<string, unknown>) => Promise<T>;

interface ArrivalContext {
  destination: { lat: number; lng: number } | null;
  settings: { radius_m: number; max_accuracy_m: number; out_of_radius: 'justify' | 'block'; photo_required: boolean };
  arrival: { at: string } | null;
}

interface Props {
  call: Caller;
  tenantSlug: string;
  order: { id: string; number: string; customer: { name: string | null; phone: string | null }; address: Record<string, string | null> };
  onClose: () => void;
  onDone: () => void;
}

const IMPEDIMENTS = ['Cliente ausente', 'Endereço não encontrado', 'Acesso negado', 'Veículo indisponível', 'Outro'];

export function ProviderArrivalScreen({ call, tenantSlug, order, onClose, onDone }: Props) {
  const ctx = useQuery({ queryKey: ['pp', tenantSlug, 'arrival', order.id], queryFn: () => call<ArrivalContext>('orders.arrival_context', { id: order.id }) });
  const [pos, setPos] = useState<{ lat: number; lng: number; accuracy: number; at: number } | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  // Guia de liberação exibido quando a localização está bloqueada (mesma folha do deslocamento).
  const [geoGuide, setGeoGuide] = useState(false);
  const [photo, setPhoto] = useState<{ file: File; url: string } | null>(null);
  const [camera, setCamera] = useState(false);
  const [justification, setJustification] = useState('');
  const [busy, setBusy] = useState(false);
  const [impediment, setImpediment] = useState<string | null>(null);
  const [impNote, setImpNote] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  // Relógio de 1 s apenas para exibir "Localização atualizada há X s".
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  const posRef = useRef(pos); posRef.current = pos;

  // Acompanha o GPS só enquanto a tela está aberta (sem rastreamento contínuo).
  useEffect(() => {
    if (!navigator.geolocation) { setGpsError('Este aparelho não oferece localização.'); return; }
    const id = navigator.geolocation.watchPosition(
      // `at` usa o horário da própria leitura do GPS (não o do recebimento).
      (p) => { setGpsError(null); setPos({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, at: p.timestamp || Date.now() }); },
      (e) => { setGpsError(e.code === e.PERMISSION_DENIED ? 'Permita o acesso à localização para confirmar a chegada.' : 'Não foi possível obter sua localização. Vá para um local aberto.'); if (e.code === e.PERMISSION_DENIED) setGeoGuide(true); },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 });
    return () => navigator.geolocation.clearWatch(id);
  }, []);
  useEffect(() => () => { if (photo) URL.revokeObjectURL(photo.url); }, [photo]);

  const cfg = ctx.data?.settings;
  const dest = ctx.data?.destination ?? null;
  const distance = pos && dest ? geoDistanceM(pos.lat, pos.lng, Number(dest.lat), Number(dest.lng)) : null;
  const state = pos && cfg ? providerArrivalState({ accuracy: pos.accuracy, distance, radius: cfg.radius_m, maxAccuracy: cfg.max_accuracy_m }) : null;
  const blocked = state === 'outside' && cfg?.out_of_radius === 'block';
  const needsJust = state === 'outside' && cfg?.out_of_radius === 'justify';
  const canConfirm = !!pos && !!cfg && state !== 'poor_accuracy' && !blocked
    && (!cfg.photo_required || !!photo) && (!needsJust || justification.trim().length >= 5) && !busy;
  const links = providerContactLinks(order.customer.phone);
  const a = order.address;
  const line1 = [a.street, a.number].filter(Boolean).join(', ');
  const line2 = [a.district, [a.city, a.state].filter(Boolean).join(' - ')].filter(Boolean).join(' • ');

  const tone = state === 'inside' ? 'text-success' : state === 'outside' || state === 'poor_accuracy' ? 'text-warning' : 'text-primary';
  const halo = state === 'inside' ? 'bg-success/10' : state === 'outside' || state === 'poor_accuracy' ? 'bg-warning/10' : 'bg-primary/10';
  const headline = gpsError ? gpsError
    : !pos ? 'Buscando sua localização…'
      : state === 'poor_accuracy' ? `Precisão do GPS insuficiente (±${Math.round(pos.accuracy)} m)`
        : state === 'no_reference' ? 'OS sem coordenadas de referência'
          : state === 'inside' ? 'Você está dentro da área do atendimento'
            : `Você está a ${Math.round(distance ?? 0)} m do local`;

  /** Confirma no servidor e, em seguida, anexa a foto ao andamento oficial de chegada. */
  const confirm = async () => {
    const last = posRef.current;
    if (!last || !cfg) return;
    setBusy(true);
    try {
      // Leitura nova no clique: watchPosition não atualiza com o aparelho parado,
      // e o tempo da foto deixava a posição com mais de 60 s (recusada pelo servidor).
      setRefreshing(true);
      let p = last;
      try {
        const fresh = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 }));
        p = { lat: fresh.coords.latitude, lng: fresh.coords.longitude, accuracy: fresh.coords.accuracy, at: fresh.timestamp || Date.now() };
        setPos(p);
      } catch {
        if (!providerPositionReusable(last.at)) {
          toast.error('Não foi possível atualizar sua localização. Tente novamente em local aberto.');
          return;
        }
      } finally { setRefreshing(false); }
      // Reconfirma a regra com a leitura usada no envio.
      const d = dest ? geoDistanceM(p.lat, p.lng, Number(dest.lat), Number(dest.lng)) : null;
      const st = providerArrivalState({ accuracy: p.accuracy, distance: d, radius: cfg.radius_m, maxAccuracy: cfg.max_accuracy_m });
      if (st === 'poor_accuracy') { toast.error(`Precisão do GPS insuficiente (±${Math.round(p.accuracy)} m). Tente novamente.`); return; }
      if (st === 'outside' && cfg.out_of_radius === 'block') { toast.error('Você está fora da área do atendimento.'); return; }
      if (st === 'outside' && cfg.out_of_radius === 'justify' && justification.trim().length < 5) { toast.error('Informe a justificativa para confirmar fora da área.'); return; }
      const res = await call<{ comment_id?: string; already?: boolean }>('orders.arrival_confirm', {
        id: order.id, latitude: p.lat, longitude: p.lng, accuracy: p.accuracy,
        position_age_s: providerPositionAgeS(p.at),
        justification: justification.trim() || undefined, has_photo: !!photo,
      });
      if (photo && res.comment_id) {
        try {
          const auth = await call<{ ticket: string; upload_url: string }>('attachments.prepare', { id: order.id, comment_id: res.comment_id, batch_id: crypto.randomUUID(), slot: 0, file: { name: photo.file.name, size: photo.file.size, type: photo.file.type } });
          const put = await fetch(auth.upload_url, { method: 'PUT', headers: { 'Content-Type': photo.file.type, 'x-upsert': 'false' }, body: photo.file });
          if (!put.ok) throw new Error('upload');
          await call('attachments.confirm', { id: order.id, ticket: auth.ticket });
        } catch {
          toast.error('Chegada registrada, mas a foto não foi enviada. Anexe-a em Andamentos.');
        }
      }
      toast.success(res.already ? 'Chegada já estava registrada' : 'Chegada confirmada');
      onDone();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const reportImpediment = async () => {
    if (!impediment) return;
    setBusy(true);
    try {
      await call('comments.add', { id: order.id, note: `Impedimento informado: ${impediment}${impNote.trim() ? ` — ${impNote.trim()}` : ''}` });
      toast.success('Impedimento registrado');
      setImpediment(null); setImpNote(''); onDone();
    } catch (error) { toast.error((error as Error).message); } finally { setBusy(false); }
  };

  const checks = [
    { label: 'GPS validado', ok: !!pos && state !== 'poor_accuracy' },
    { label: 'Dentro da área', ok: state === 'inside', hidden: state === 'no_reference' },
    { label: cfg?.photo_required ? 'Foto da fachada (obrigatória)' : 'Foto da fachada', ok: !!photo },
  ].filter((c) => !c.hidden);

  return <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
    <SheetContent side="bottom" enableSwipeToClose={false} aria-describedby={undefined} className="provider-app mx-auto flex h-[100dvh] w-full max-w-2xl flex-col gap-3 overflow-y-auto bg-background p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] text-foreground sm:h-[90dvh] sm:rounded-t-lg">
      <SheetHeader className="pr-8 text-left"><SheetTitle className="text-lg font-bold">Chegada ao local</SheetTitle></SheetHeader>

      <section className="space-y-3 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold">Destino da OS</h2>
        <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary"><User className="h-5 w-5" /></span><div className="min-w-0"><p className="truncate text-sm font-semibold">{order.customer.name ?? 'Cliente'}</p>{order.customer.phone && <p className="text-xs text-muted-foreground">{order.customer.phone}</p>}</div></div>
        <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-foreground"><MapPin className="h-5 w-5" /></span><div className="min-w-0"><p className="truncate text-sm font-semibold">{line1 || 'Endereço não informado'}</p>{line2 && <p className="text-xs text-muted-foreground">{line2}</p>}</div></div>
      </section>

      <section className="rounded-lg border bg-card p-4 text-center">
        {ctx.isLoading ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin" /> : <>
          <span className={cn('mx-auto flex h-28 w-28 items-center justify-center rounded-full', halo)}>
            <span className={cn('flex h-16 w-16 items-center justify-center rounded-full', halo, !pos && !gpsError && 'animate-pulse')}><MapPin className={cn('provider-icon-breathe h-10 w-10', tone)} /></span>
          </span>
          <p className="mt-3 text-base font-bold">{headline}</p>
          {pos && <p className={cn('mt-1 text-xs font-medium', tone)}>
            {distance != null ? `${Math.round(distance)} m do ponto da OS · ` : ''}Precisão ±{Math.round(pos.accuracy)} m{cfg ? ` · Raio ${cfg.radius_m} m` : ''} · Localização atualizada há {providerPositionAgeS(pos.at, now)} s
          </p>}
          {blocked && <p className="mt-2 text-xs text-destructive">Aproxime-se do local para confirmar a chegada.</p>}
          <ul className="mt-4 divide-y rounded-md border text-left">
            {checks.map((c) => <li key={c.label} className="flex items-center gap-3 px-3 py-2.5 text-sm">
              <CheckCircle2 className={cn('h-5 w-5', c.ok ? 'text-success' : 'text-muted-foreground/40')} />{c.label}
            </li>)}
          </ul>
        </>}
      </section>

      {needsJust && <Textarea value={justification} maxLength={500} onChange={(e) => setJustification(e.target.value)} placeholder="Justifique a chegada fora da área (obrigatório)" aria-label="Justificativa" />}

      {photo && <img src={photo.url} alt="Foto da fachada" className="max-h-48 w-full rounded-lg border object-cover" />}

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" className="h-14" disabled={!links} onClick={() => { if (links) window.location.href = links.call; }}><Phone className="mr-2 h-4 w-4" />Ligar para o cliente</Button>
        <Button variant="outline" className="h-14 whitespace-normal" disabled={!pos} onClick={() => setCamera(true)}><Camera className="mr-2 h-4 w-4 shrink-0" />{photo ? 'Refazer foto' : 'Registrar foto da fachada'}</Button>
      </div>
      <Button className="h-12 bg-success text-success-foreground hover:bg-success/90" disabled={!canConfirm} onClick={confirm}>
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}{refreshing ? 'Atualizando localização…' : 'Confirmar chegada'}
      </Button>
      <Button variant="link" className="text-primary" onClick={() => setImpediment(IMPEDIMENTS[0])}><AlertTriangle className="mr-1 h-4 w-4" />Informar impedimento</Button>

      {impediment && <section className="space-y-2 rounded-lg border bg-card p-4">
        <h3 className="text-sm font-semibold">Qual é o impedimento?</h3>
        <div className="flex flex-wrap gap-2">{IMPEDIMENTS.map((r) => <Button key={r} size="sm" variant={r === impediment ? 'default' : 'outline'} onClick={() => setImpediment(r)}>{r}</Button>)}</div>
        <Textarea value={impNote} maxLength={500} onChange={(e) => setImpNote(e.target.value)} placeholder="Observação (opcional)" />
        <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={() => setImpediment(null)}>Cancelar</Button><Button className="flex-1" disabled={busy} onClick={reportImpediment}>Registrar</Button></div>
      </section>}

      {/* Após liberar nos ajustes, recarregar reinicia o acompanhamento do GPS. */}
      <ProviderLocationPermissionSheet open={geoGuide} state="denied" reason=""
        onRequest={() => window.location.reload()} onClose={() => setGeoGuide(false)} />
      <CameraCaptureDialog open={camera} onOpenChange={setCamera} title="Foto da fachada"
        description="A foto recebe as coordenadas, a precisão e a distância até o ponto da OS."
        watermark={() => { const p = posRef.current; return p ? arrivalWatermarkLines({ at: new Date(), lat: p.lat, lng: p.lng, accuracy: p.accuracy, destination: dest ? { lat: Number(dest.lat), lng: Number(dest.lng) } : null, distance, orderNumber: order.number }) : []; }}
        onCapture={(file) => { setPhoto((old) => { if (old) URL.revokeObjectURL(old.url); return { file, url: URL.createObjectURL(file) }; }); return true; }} />
    </SheetContent>
  </Sheet>;
}
